import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import useReveal from '../../hooks/useReveal';
import useChartReveal from '../../hooks/useChartReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

interface EducationRendererProps {
  title: string;
  data: any;
  primaryColor?: string;
}

export default function EducationRenderer({ title, data, primaryColor = '#14B8A6' }: EducationRendererProps) {
  if (!data) return null;
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const chartRef = useChartReveal(isVisible);

  // Handle both array and object formats
  let items: Array<{ label: string; value: number }> = [];
  
  if (Array.isArray(data)) {
    items = data.map((item: any) => ({
      label: item.label || item.name || String(item),
      value: typeof item.value === 'number' ? item.value : (typeof item.composition === 'number' ? item.composition : 0)
    }));
  } else if (data && typeof data === 'object') {
    items = Object.entries(data).map(([key, value]) => ({
      label: key,
      value: typeof value === 'number' ? value : 0
    }));
  }

  if (items.length === 0) return null;

  const labels = items.map(i => i.label);
  const values = items.map(i => i.value);

  const barData = {
    labels,
    datasets: [{
      data: values,
      backgroundColor: primaryColor,
      borderRadius: { topLeft: 30, topRight: 30 } as any,
      barThickness: 60
    }]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      x: {
        ticks: { 
          color: '#6B7280',
          font: { size: 10 },
          maxRotation: 45,
          minRotation: 0
        },
        grid: { display: false }
      },
      y: {
        ticks: { 
          color: '#6B7280',
          font: { size: 10 }
        },
        beginAtZero: true,
        max: 50,
        grid: { color: '#E5E7EB' }
      }
    }
  };

  return (
    <div
      ref={ref}
      className="bg-white rounded-lg shadow-md p-6"
      style={{
        transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        opacity: isVisible ? 1 : 0.25,
        transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
      }}
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <div className="text-xs text-gray-500 mb-2">% COMPOSITION</div>
      <div className="text-xs text-gray-500 mb-4">Online Adult Population</div>
      <div className="h-80">
        <div
          className="h-full"
          style={{
            transition: `opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
            transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
            opacity: isVisible ? 1 : 0,
          }}
        >
          <Bar
            ref={chartRef}
            data={barData}
            options={{
              ...barOptions,
              animation: {
                duration: isVisible ? BAR_ANIMATION_MS : 0,
                easing: 'easeOutCubic',
                delay: isVisible ? REVEAL_DELAY_MS : 0,
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
